import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import messageToString from '../functions/messageToString';
import MagicLinkSignInMessagesPropType from '../definitions/MagicLinkSignInMessagesPropType';
import useSession from '../hooks/useSessionStore';

import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import getTermsURL from '../functions/getTermsURL';
import getPrivacyPolicyURL from '../functions/getPrivacyPolicyURL';
import Form from './Form';
import PrimaryButton from './PrimaryButton';
import GoogleSignInButton from './GoogleSignInButton';
import CondensedTheme from './CondensedTheme';
import EmailInput from './EmailInput';


function MagicLinkSignIn({
    email,
    onSubmit,
    messages,
    shouldShowGoogleSignIn = true,
    onGoogleSignIn,
    children,
}) {
    const intl = useIntl();
    const sessionStore = useSession();

    const defaultEmail = sessionStore?.hasFetchedSession && !sessionStore?.isLoggedIn
        ? sessionStore.email
        : undefined;

    function handleGoogleSignIn(providerToken) {
        return onGoogleSignIn?.(providerToken);
    }

    return (
        <StyledForm
            onSubmit={({ email: submittedEmail }) => onSubmit(submittedEmail)}
            shouldOnlyButtonSubmit={false}
            className="rh-display-flex rh-flex-direction-column rh-align-items-center"
        >
            <CondensedTheme>
                <h1 className="rh-title-l rh-text-align-center">
                    {messageToString(messages?.MAGIC_LINK_SIGN_IN_TITLE ?? MESSAGES.MAGIC_LINK_SIGN_IN_TITLE, intl)}
                </h1>

                {children}

                <div className="same-width rh-width-100p">
                    <If condition={shouldShowGoogleSignIn}>
                        <div className="rh-display-flex rh-justify-content-center rh-mb-1">
                            <GoogleSignInButton
                                locale={intl.locale}
                                onSignIn={handleGoogleSignIn}
                            />
                        </div>

                        <div
                            className="divider rh-display-flex rh-align-items-center rh-text-align-center rh-my-2 rh-text-s"
                        >
                            <FormattedMessage
                                id="accounts.magic-sign-in.sign-in-option-divider"
                                defaultMessage="or"
                            />
                        </div>
                    </If>

                    <EmailInput
                        name="email"
                        value={email ?? defaultEmail}
                        label={MESSAGES.EMAIL_LABEL}
                        errorMessageStyle="minimal"
                        className="rh-mb-1"
                    />

                    <PrimaryButton
                        type="submit"
                        size="small"
                        className="submit"
                        data-name="request-magic-link"
                        message={messages?.MAGIC_LINK_SIGN_IN_CTA ?? MESSAGES.MAGIC_LINK_SIGN_IN_CTA}
                    />

                    <p
                        className="rh-text-align-center rh-text-s"
                    >
                        <FormattedMessage
                            {...MESSAGES.DESCRIPTION}
                        />
                    </p>

                    <p className="rh-text-align-center rh-text-s rh-mt-4 rh-fg-blackberry-light">
                        <FormattedMessage
                            id="Accounts.MagicLinkSignIn.Disclaimer"
                            defaultMessage="<terms>Terms of Service</terms> | <privacy>Privacy Policy</privacy>"
                            values={{
                                terms: msg => (
                                    <a
                                        href={getTermsURL(intl.locale)}
                                        target="_blank"
                                        className="rh-fg-blackberry-light  hover--rh-fg-blueberry-dark"
                                    >
                                        {msg}
                                    </a>
                                ),
                                privacy: msg => (
                                    <a
                                        href={getPrivacyPolicyURL(intl.locale)}
                                        target="_blank"
                                        className="rh-fg-blackberry-light  hover--rh-fg-blueberry-dark"
                                    >
                                        {msg}
                                    </a>
                                ),
                            }}
                        />
                    </p>
                </div>
            </CondensedTheme>
        </StyledForm>
    );
}

const StyledForm = styled(Form)`
    /* Google SSO button can only be up to 400px but we prefer 300px */
    .same-width {
        max-width: 300px;
    }

    .divider {
        &::before,
        &::after {
            content: '';
            flex: 1;
            border-bottom: 1px solid ${Colours.STONE_DARK};
        }

        &:not(:empty) {
            &::before {
                margin-right: ${Sizes.SPACING.ONE};
            }
            &::after {
                margin-left: ${Sizes.SPACING.ONE};
            }
        }
    }
`;

MagicLinkSignIn.propTypes = {
    email: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,

    messages: MagicLinkSignInMessagesPropType,
    shouldShowGoogleSignIn: PropTypes.bool,
    onGoogleSignIn: PropTypes.func,

    children: PropTypes.node, // Allows injecting custom content into the modal
};

const MESSAGES = defineMessages({
    MAGIC_LINK_SIGN_IN_TITLE: {
        id: 'Accounts.MagicLinkSignIn.Title',
        defaultMessage: 'Sign-in or create an account',
    },
    MAGIC_LINK_SIGN_IN_CTA: {
        id: 'Accounts.MagicLinkSignIn.CTA',
        defaultMessage: 'Send a sign-in link',
    },
    EMAIL_LABEL: {
        id: 'Accounts.MagicLinkSignIn.EmailLabel',
        defaultMessage: 'Email address',
    },
    EMAIL_PLACEHOLDER: {
        id: 'Accounts.MagicLinkSignIn.EmailPlaceholder',
        defaultMessage: 'Enter email',
    },
    DESCRIPTION: {
        id: 'Accounts.MagicLinkSignIn.Description',
        defaultMessage: 'We will send you a direct link to securely sign in without a password.',
    },
});

export default observer(MagicLinkSignIn);
